import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { BodyText, Button, CloudinaryImage, Container, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeOrderCard = (props: HomeOrderCardProps) => {
  const { title, description, image, imageAlt, cta, orderCount, onPress } = props;
  const { match } = useResponsive();

  // ───────────────────────────────────────────────────────────────────────────

  const Wrapper = match([Container, View]);
  const additionalItemsCount = orderCount - 1;
  const shouldShowItemCount = Boolean(image) && additionalItemsCount > 0;

  return (
    <Wrapper style={match([styles.containerXS, styles.containerSM])}>
      <View style={styles.card}>
        <View style={styles.content}>
          <View style={styles.info}>
            <BodyText sizeMatch={['16']}>{title}</BodyText>

            <BodyText style={styles.description} sizeMatch={['14']}>
              {description}
            </BodyText>
          </View>

          {image ? (
            <CloudinaryImage
              baseUrl={image}
              alt={imageAlt}
              config={IMAGE_CONFIG}
              style={styles.image}
            />
          ) : null}

          {shouldShowItemCount ? (
            <BodyText sizeMatch={['12']} style={styles.orderCount}>
              + {additionalItemsCount}
            </BodyText>
          ) : null}
        </View>

        <Button palette="secondary" accessibilityLabel={cta} onPress={onPress}>
          {cta}
        </Button>
      </View>
    </Wrapper>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const IMAGE_SIZE = 56;
const IMAGE_CONFIG = [{ crop: 'crop' as const, width: 0.95, height: 0.95 }, { width: IMAGE_SIZE }];

const styles = StyleSheet.create({
  containerXS: {
    marginTop: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  containerSM: {
    width: 343,
  },
  card: {
    padding: theme.spacing['6'],
    gap: theme.spacing['6'],
    borderRadius: theme.radius.xlarge,
    borderWidth: 1,
    borderColor: theme.colors.LIGHT,
    backgroundColor: theme.colors.WHITE,
  },
  content: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  info: {
    flex: 1,
    flexWrap: 'wrap',
    gap: theme.spacing['2'],
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.medium,
  },
  orderCount: {
    position: 'absolute',
    bottom: theme.spacing['1'],
    right: theme.spacing['1'],
    fontSize: 10,
    lineHeight: 16,
    letterSpacing: -1,
    paddingVertical: 1,
    paddingHorizontal: theme.spacing['1'],
    borderRadius: theme.radius.small,
    color: theme.colors.WHITE,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARKER,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeOrderCardProps = {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  cta: string;
  orderCount: number;
  onPress: () => void;
};
